/**
 * Receipt date/time semantics: the operator always enters Europe/London site time, whatever the browser's zone.
 * Conversions use the runtime's IANA rules through Intl (no library default policy): a wall-clock time that does not
 * exist (spring gap) is reported, and one that happens twice (autumn overlap) is reported with both instants so the
 * operator can say which one they mean.
 */
export const SITE_TIME_ZONE = "Europe/London";

/** Which occurrence of a repeated wall-clock time is meant during the autumn clock change. */
export type ReceiptTimeOccurrence = "" | "earlier" | "later";

export type ReceiptInstantCandidate = {
    iso: string;
    offsetMinutes: number;
    /** Zone label for the operator: BST or GMT (derived from the offset, so it reads the same in every browser). */
    zoneLabel: string;
};

export type LondonInstantResolution =
    | { kind: "invalid" }
    | { kind: "nonexistent" }
    | { kind: "unique"; instant: ReceiptInstantCandidate }
    | { kind: "ambiguous"; earlier: ReceiptInstantCandidate; later: ReceiptInstantCandidate };

type WallClock = { year: number; month: number; day: number; hour: number; minute: number };

const partsFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: SITE_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
});

function wallClockInSiteZone(instantMs: number): WallClock {
    const parts = partsFormatter.formatToParts(new Date(instantMs));
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
    return {
        year: Number(get("year")),
        month: Number(get("month")),
        day: Number(get("day")),
        hour: Number(get("hour")) % 24,
        minute: Number(get("minute")),
    };
}

function zoneLabelForOffset(offsetMinutes: number): string {
    if (offsetMinutes === 0) return "GMT";
    if (offsetMinutes === 60) return "BST";
    const sign = offsetMinutes < 0 ? "-" : "+";
    const abs = Math.abs(offsetMinutes);
    return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

function siteZoneOffsetMinutes(instantMs: number): number {
    const minuteMs = Math.floor(instantMs / 60000) * 60000; // the wall clock is read to the minute
    const w = wallClockInSiteZone(minuteMs);
    return Math.round((Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute) - minuteMs) / 60000);
}

function parseWallClock(date: string, time: string): WallClock | null {
    const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date ?? "").trim());
    const t = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec((time ?? "").trim());
    if (!d || !t) return null;
    const wall = { year: Number(d[1]), month: Number(d[2]), day: Number(d[3]), hour: Number(t[1]), minute: Number(t[2]) };
    if (wall.month < 1 || wall.month > 12 || wall.day < 1 || wall.day > 31 || wall.hour > 23 || wall.minute > 59) return null;
    // Reject calendar overflow (e.g. 31 April) by round-tripping through Date.UTC.
    const check = new Date(Date.UTC(wall.year, wall.month - 1, wall.day));
    if (check.getUTCFullYear() !== wall.year || check.getUTCMonth() !== wall.month - 1 || check.getUTCDate() !== wall.day) return null;
    return wall;
}

function sameWallClock(a: WallClock, b: WallClock): boolean {
    return a.year === b.year && a.month === b.month && a.day === b.day && a.hour === b.hour && a.minute === b.minute;
}

/** Resolves an operator-entered Europe/London date and time to the real instant(s) it denotes. */
export function resolveLondonInstant(date: string, time: string): LondonInstantResolution {
    const wall = parseWallClock(date, time);
    if (!wall) return { kind: "invalid" };

    const naiveUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
    const dayMs = 24 * 60 * 60 * 1000;
    // Every offset the zone uses around this date (both sides of any transition) is a candidate interpretation.
    const offsets = Array.from(new Set([naiveUtc - dayMs, naiveUtc, naiveUtc + dayMs].map(siteZoneOffsetMinutes)));

    const matches: ReceiptInstantCandidate[] = [];
    for (const offset of offsets) {
        const candidateMs = naiveUtc - offset * 60000;
        const observed = wallClockInSiteZone(candidateMs);
        const iso = new Date(candidateMs).toISOString();
        if (sameWallClock(observed, wall) && !matches.some((m) => m.iso === iso)) {
            matches.push({ iso, offsetMinutes: offset, zoneLabel: zoneLabelForOffset(offset) });
        }
    }

    if (matches.length === 0) return { kind: "nonexistent" };
    if (matches.length === 1) return { kind: "unique", instant: matches[0] };
    matches.sort((a, b) => Date.parse(a.iso) - Date.parse(b.iso));
    return { kind: "ambiguous", earlier: matches[0], later: matches[1] };
}

/**
 * Europe/London date + time → ISO instant for the API. Null when the parts are missing/invalid, when the time does
 * not exist on that date, or when it is ambiguous and no occurrence has been chosen.
 */
export function combineReceiptDateTime(date: string, time: string, occurrence: ReceiptTimeOccurrence | string = ""): string | null {
    const resolution = resolveLondonInstant(date, time);
    if (resolution.kind === "unique") return resolution.instant.iso;
    if (resolution.kind === "ambiguous") {
        if (occurrence === "earlier") return resolution.earlier.iso;
        if (occurrence === "later") return resolution.later.iso;
    }
    return null;
}

/** ISO instant → the Europe/London date and time to show, plus which occurrence it was if that time repeated. */
export function splitReceiptDateTime(iso: string | null | undefined): { date: string; time: string; occurrence: ReceiptTimeOccurrence } {
    if (!iso) return { date: "", time: "", occurrence: "" };
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return { date: "", time: "", occurrence: "" };
    const w = wallClockInSiteZone(ms);
    const date = `${w.year.toString().padStart(4, "0")}-${w.month.toString().padStart(2, "0")}-${w.day.toString().padStart(2, "0")}`;
    const time = `${w.hour.toString().padStart(2, "0")}:${w.minute.toString().padStart(2, "0")}`;
    const resolution = resolveLondonInstant(date, time);
    let occurrence: ReceiptTimeOccurrence = "";
    if (resolution.kind === "ambiguous") {
        const minuteInstant = Math.floor(ms / 60000) * 60000;
        occurrence = Date.parse(resolution.earlier.iso) === minuteInstant ? "earlier" : "later";
    }
    return { date, time, occurrence };
}

/**
 * The current Europe/London date and wall-clock time as the editable starting point for a new receipt, with the
 * occurrence already known when "now" falls inside the hour that repeats as the clocks go back.
 */
export function currentLondonReceiptDateTime(now: Date = new Date()): { date: string; time: string; occurrence: ReceiptTimeOccurrence } {
    return splitReceiptDateTime(now.toISOString());
}
